import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Grid,
  InputAdornment,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 10;
const EMPTY_RESULT = {
  data: [],
  total: 0,
  page: 1,
  limit: PAGE_SIZE,
  totalPages: 0,
};

const ProfileSkeleton = () => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <Card>
      <Skeleton variant="rectangular" height={210} />
      <CardContent>
        <Skeleton width="70%" />
        <Skeleton width="50%" />
        <Skeleton width="80%" />
      </CardContent>
    </Card>
  </Grid>
);

const MatchProfilePage = ({title, fetchProfiles}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    let active = true;

    const loadProfiles = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetchProfiles(page, PAGE_SIZE);
        if (active) {
          setResult({...EMPTY_RESULT, ...response.data});
        }
      } catch (requestError) {
        if (active) {
          setResult({...EMPTY_RESULT, page});
          setError(
            requestError?.response?.data?.error?.message ||
              requestError?.message ||
              "Unable to load profiles. Please try again.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadProfiles();
    return () => {
      active = false;
    };
  }, [fetchProfiles, page, requestVersion]);

  const visibleProfiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return result.data;
    return result.data.filter((profile) =>
      (profile.name || "").toLowerCase().includes(query),
    );
  }, [result.data, search]);

  const handlePageChange = (_, nextPage) => {
    setPage(nextPage);
    setSearch("");
    window.scrollTo({top: 0, behavior: "smooth"});
  };

  return (
    <Box>
      <Stack
        direction={{xs: "column", sm: "row"}}
        justifyContent="space-between"
        alignItems={{xs: "stretch", sm: "center"}}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5">{title}</Typography>
          {!loading && !error && (
            <Typography variant="body2" color="text.secondary">
              {result.total} {result.total === 1 ? "profile" : "profiles"}
            </Typography>
          )}
        </Box>
        <TextField
          size="small"
          label="Search this page"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => setRequestVersion((version) => version + 1)}
            >
              Retry
            </Button>
          }
          sx={{mb: 3}}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {loading ? (
          Array.from({length: 8}, (_, index) => (
            <ProfileSkeleton key={index} />
          ))
        ) : visibleProfiles.length ? (
          visibleProfiles.map((profile) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={profile.id}>
              <Card sx={{height: "100%"}}>
                <CardActionArea
                  onClick={() =>
                    navigate(`/auth/home/view-profile/${profile.id}`)
                  }
                  sx={{height: "100%"}}
                >
                  {profile.image ? (
                    <CardMedia
                      component="img"
                      height="210"
                      image={profile.image}
                      alt={profile.name || "Profile photo"}
                      sx={{objectFit: "cover"}}
                    />
                  ) : (
                    <Box
                      height={210}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="grey.200"
                    >
                      <Typography color="text.secondary">
                        No image available
                      </Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {profile.name || profile.profile_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {profile.age != null
                        ? `${profile.age} yrs`
                        : "Age not provided"}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <SchoolIcon fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {profile.education || "Education not provided"}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      mt={0.5}
                    >
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {[profile.city, profile.country]
                          .filter(Boolean)
                          .join(", ") || "Location not provided"}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))
        ) : !error ? (
          <Grid item xs={12}>
            <Box textAlign="center" py={8}>
              <Typography variant="h6">
                {search.trim()
                  ? "No profiles on this page match your search"
                  : "No matching profiles found"}
              </Typography>
            </Box>
          </Grid>
        ) : null}
      </Grid>

      {!loading && !error && result.totalPages > 1 && (
        <Stack alignItems="center" mt={5} mb={3}>
          <Pagination
            count={result.totalPages}
            page={result.page}
            color="primary"
            onChange={handlePageChange}
          />
        </Stack>
      )}
    </Box>
  );
};

export default MatchProfilePage;
